import { Icon } from './Icon';
import {
  PET_ORIGIN_OPTIONS,
  PET_COLLECTION_DEFAULT_FILTERS,
  type PetCollectionFiltersState,
  type PetCollectionSort,
  type PetOriginCounts,
  type PetOriginFilter,
  type PetOriginOption,
  type PetMergeMode,
  type MergeProgress,
} from '@/ui/hooks/usePetCollectionView';
import { biomeEnvironmentAssetUrl } from '@/assets/paths';
import { getPetOriginBiomeVisual } from '@/ui/petOriginVisuals';
import { useId, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';

interface SortOption {
  value: PetCollectionSort;
  label: string;
}

interface PetCollectionFiltersProps {
  filters: PetCollectionFiltersState;
  onChange: (patch: Partial<PetCollectionFiltersState>) => void;
  onReset?: () => void;
  totalCount: number;
  visibleCount: number;
  sortOptions: readonly SortOption[];
  showAffordable?: boolean;
  showPriceRange?: boolean;
  showMergeModes?: boolean;
  showReset?: boolean;
  showResults?: boolean;
  originCounts?: PetOriginCounts;
  children?: ReactNode;
}

interface VisualOption {
  value: string;
  label: string;
  icon: ReactNode;
  count?: number;
  muted?: boolean;
}

type OriginVisualGroup = 'quick' | 'worlds' | 'special' | 'events';

interface OriginVisualOption extends PetOriginOption {
  count?: number;
  muted: boolean;
  group: OriginVisualGroup;
}

export const PET_SORT_OPTIONS_BASIC: readonly SortOption[] = [
  { value: 'power', label: 'Moc' },
  { value: 'mergeProgress', label: 'Najbliżej ulepszenia' },
  { value: 'rarity', label: 'Rzadkość' },
  { value: 'variant', label: 'Wariant' },
  { value: 'acquired', label: 'Najnowsze' },
  { value: 'name', label: 'Nazwa A–Z' },
];

export const PET_SORT_OPTIONS_WITH_PRICE: readonly SortOption[] = [
  { value: 'priceAsc', label: 'Cena rosnąco' },
  { value: 'priceDesc', label: 'Cena malejąco' },
  { value: 'mergeProgress', label: 'Najbliżej ulepszenia' },
  { value: 'power', label: 'Moc' },
  { value: 'rarity', label: 'Rzadkość' },
  { value: 'variant', label: 'Wariant' },
  { value: 'name', label: 'Nazwa A–Z' },
];

const RARITY_FILTER_OPTIONS: readonly VisualOption[] = [
  { value: 'all', label: 'Wszystkie', icon: <AllMarks /> },
  { value: 'common', label: 'Common', icon: <RarityMark value="common" /> },
  { value: 'uncommon', label: 'Uncommon', icon: <RarityMark value="uncommon" /> },
  { value: 'rare', label: 'Rare', icon: <RarityMark value="rare" /> },
  { value: 'epic', label: 'Epic', icon: <RarityMark value="epic" /> },
  { value: 'legendary', label: 'Legendary', icon: <RarityMark value="legendary" /> },
  { value: 'mythic', label: 'Mythic', icon: <RarityMark value="mythic" /> },
];

const VARIANT_FILTER_OPTIONS: readonly VisualOption[] = [
  { value: 'all', label: 'Wszystkie', icon: <AllMarks /> },
  { value: 'normal', label: 'Normal', icon: <VariantMark value="normal" /> },
  { value: 'gold', label: 'Gold', icon: <VariantMark value="gold" /> },
  { value: 'rainbow', label: 'Rainbow', icon: <VariantMark value="rainbow" /> },
  { value: 'huge', label: 'HUGE', icon: <VariantMark value="huge" /> },
  { value: 'titanic', label: 'TITANIC', icon: <VariantMark value="titanic" /> },
];

export function PetCollectionFilters({
  filters,
  onChange,
  onReset,
  totalCount,
  visibleCount,
  sortOptions,
  showAffordable = false,
  showPriceRange = false,
  showMergeModes = true,
  showReset = true,
  showResults = true,
  originCounts,
  children,
}: PetCollectionFiltersProps): JSX.Element {
  const reset = (): void => {
    if (onReset) onReset();
    else onChange(PET_COLLECTION_DEFAULT_FILTERS);
  };
  const sortVisualOptions = sortOptions.map(toVisualSortOption);
  const originVisualOptions = PET_ORIGIN_OPTIONS.map((option) => toOriginVisualOption(option, originCounts));

  return (
    <div className="mb-3 rounded-2xl border border-meadow-100 bg-meadow-50/70 p-2.5 text-xs">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative min-w-0 basis-full sm:min-w-[160px] sm:flex-1 sm:basis-auto">
            <Icon
              src="/ui/ui_btn_search.webp"
              fallback="?"
              alt=""
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70"
            />
            <input
              value={filters.query}
              onChange={(e) => onChange({ query: e.target.value })}
              placeholder="Szukaj peta"
              aria-label="Szukaj peta"
              className="h-9 w-full rounded-xl border border-meadow-200 bg-white pl-8 pr-3 font-bold text-meadow-900 outline-none focus:border-meadow-500"
            />
          </label>

          <VisualSelectControl
            label="Ułóż pety"
            value={filters.sortBy}
            onChange={(value) => onChange({ sortBy: value as PetCollectionSort })}
            options={sortVisualOptions}
          />

          <PetOriginPicker
            label="Pety z"
            value={filters.originFilter}
            onChange={(value) => onChange({ originFilter: value as PetOriginFilter })}
            options={originVisualOptions}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <VisualSelectControl
            label="Rzadkość"
            value={filters.rarity}
            onChange={(value) => onChange({ rarity: value as PetCollectionFiltersState['rarity'] })}
            options={RARITY_FILTER_OPTIONS}
          />

          <VisualSelectControl
            label="Wariant"
            value={filters.variant}
            onChange={(value) => onChange({ variant: value as PetCollectionFiltersState['variant'] })}
            options={VARIANT_FILTER_OPTIONS}
          />

          {showMergeModes && (
            <div className="flex items-center gap-1.5">
              <span className="text-gray-600">Merge:</span>
              <MergeButton
                value="all"
                current={filters.mergeMode}
                label="Wszystkie"
                onClick={() => onChange({ mergeMode: 'all', sortBy: filters.sortBy === 'mergeProgress' ? 'power' : filters.sortBy })}
              />
              <MergeButton
                value="gold"
                current={filters.mergeMode}
                label="Do golda"
                onClick={() => onChange({ mergeMode: 'gold', sortBy: 'mergeProgress', variant: 'all' })}
              />
              <MergeButton
                value="rainbow"
                current={filters.mergeMode}
                label="Do rainbow"
                onClick={() => onChange({ mergeMode: 'rainbow', sortBy: 'mergeProgress', variant: 'all' })}
              />
            </div>
          )}

          {showAffordable && (
            <label className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 font-bold text-meadow-700">
              <input
                type="checkbox"
                checked={filters.affordableOnly}
                onChange={(e) => onChange({ affordableOnly: e.target.checked })}
              />
              Stać mnie
            </label>
          )}

          {showPriceRange && (
            <div className="flex items-center gap-1.5">
              <span className="text-gray-600">Cena:</span>
              <input
                value={filters.priceMin}
                onChange={(e) => onChange({ priceMin: e.target.value })}
                inputMode="numeric"
                placeholder="od"
                className="h-8 w-16 rounded-lg border border-meadow-200 bg-white px-2 font-bold outline-none focus:border-meadow-500"
              />
              <input
                value={filters.priceMax}
                onChange={(e) => onChange({ priceMax: e.target.value })}
                inputMode="numeric"
                placeholder="do"
                className="h-8 w-16 rounded-lg border border-meadow-200 bg-white px-2 font-bold outline-none focus:border-meadow-500"
              />
            </div>
          )}

          {children}
        </div>

        {(showResults || showReset) && (
          <div className="flex items-center justify-between gap-2 text-[11px] font-bold text-gray-500">
            {showResults ? (
              <span>
                Wyniki: {visibleCount}/{totalCount}
              </span>
            ) : <span />}
            {showReset && (
              <button
                type="button"
                onClick={reset}
                className="rounded-lg bg-white px-2 py-1 text-meadow-700 transition hover:bg-meadow-100 active:scale-95"
              >
                Wyczyść filtry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function PetMergeProgressBadge({
  progress,
  active,
}: {
  progress: MergeProgress | null;
  active: boolean;
}): JSX.Element | null {
  if (!active || !progress) return null;
  return (
    <span
      className={`absolute bottom-1 right-1 z-20 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold shadow pointer-events-none ${
        progress.ready
          ? 'bg-purple-600 text-white'
          : 'bg-white text-purple-700 ring-1 ring-purple-200'
      }`}
      title={`Merge: ${progress.label}`}
    >
      {progress.label}
    </span>
  );
}

function toVisualSortOption(option: SortOption): VisualOption {
  return {
    ...option,
    icon: <SortMark value={option.value} />,
  };
}

function toOriginVisualOption(
  option: PetOriginOption,
  originCounts?: PetOriginCounts,
): OriginVisualOption {
  const visual = option.biomeId ? getPetOriginBiomeVisual(option.biomeId) : undefined;
  const group: OriginVisualGroup =
    option.kind === 'all' || option.kind === 'worlds' || option.kind === 'events'
      ? 'quick'
      : option.kind === 'event'
        ? 'events'
        : visual?.section === 'special'
          ? 'special'
          : 'worlds';
  const count = originCounts ? originCounts.get(option.value) ?? 0 : undefined;

  return {
    ...option,
    count,
    muted: option.kind !== 'all' && count === 0,
    group,
  };
}

function PetOriginPicker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly OriginVisualOption[];
}): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((option) => option.value === value) ?? options[0];
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === selected.value));
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const listboxId = `${id}-listbox`;

  const closeOnBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !event.currentTarget.contains(next)) setOpen(false);
  };
  const openAt = (index: number): void => {
    setActiveIndex(index);
    setOpen(true);
  };
  const selectAt = (index: number): void => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  };
  const moveActive = (direction: 1 | -1): void => {
    setActiveIndex((previous) => {
      const next = previous + direction;
      if (next < 0) return options.length - 1;
      if (next >= options.length) return 0;
      return next;
    });
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        if (!open) openAt(selectedIndex);
        else moveActive(1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        if (!open) openAt(selectedIndex);
        else moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        openAt(0);
        break;
      case 'End':
        event.preventDefault();
        openAt(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) selectAt(activeIndex);
        else openAt(selectedIndex);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const renderTile = (option: OriginVisualOption, quick = false): JSX.Element => {
    const index = options.indexOf(option);
    const active = option.value === value;
    const focused = index === activeIndex;
    const countLabel = option.count === undefined ? '' : `, ${option.count} petów`;

    return (
      <button
        key={option.value}
        id={`${id}-option-${option.value}`}
        type="button"
        role="option"
        aria-selected={active}
        aria-label={`${option.label}${countLabel}`}
        onMouseDown={(event) => event.preventDefault()}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => {
          onChange(option.value);
          setOpen(false);
        }}
        style={!active && option.accentColor ? { backgroundColor: `${option.accentColor}24` } : undefined}
        className={`relative flex w-full rounded-xl border text-left font-bold outline-none transition active:scale-[0.98] ${
          quick
            ? 'min-h-[76px] flex-col items-center justify-center gap-1 px-2 py-2'
            : 'min-h-[62px] items-center gap-2.5 px-2.5 py-2'
        } ${
          active
            ? 'border-meadow-500 bg-meadow-50 text-meadow-800 ring-2 ring-meadow-300'
            : focused
              ? 'border-meadow-300 bg-meadow-50 text-meadow-800 ring-2 ring-meadow-100'
              : 'border-gray-100 bg-white text-meadow-700 hover:border-meadow-200 hover:bg-meadow-50'
        } ${option.muted && !active ? 'opacity-60' : ''}`}
      >
        <PetOriginMark option={option} compact={quick} />
        <span className={quick ? 'max-w-full truncate text-center text-[11px]' : 'min-w-0 flex-1 leading-tight'}>
          {option.label}
        </span>
        {option.count !== undefined && (
          <span
            className={`${quick ? 'absolute right-1.5 top-1.5' : 'ml-auto'} shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${
              option.count === 0
                ? 'bg-gray-100 text-gray-500'
                : 'bg-white/90 text-meadow-700 shadow-sm'
            }`}
          >
            {option.count}
          </span>
        )}
        {active && (
          <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-meadow-500 shadow ring-2 ring-white">
            <Icon src="/ui/ui_btn_confirm.webp" fallback="✓" alt="wybrane" className="h-3.5 w-3.5" />
          </span>
        )}
      </button>
    );
  };

  const renderSection = (group: Exclude<OriginVisualGroup, 'quick'>, heading: string): JSX.Element | null => {
    const sectionOptions = options.filter((option) => option.group === group);
    if (sectionOptions.length === 0) return null;
    return (
      <section role="group" aria-labelledby={`${id}-${group}-heading`}>
        <h3
          id={`${id}-${group}-heading`}
          className="mb-1.5 mt-2 px-1 text-[10px] font-extrabold uppercase tracking-wide text-gray-500"
        >
          {heading}
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {sectionOptions.map((option) => renderTile(option))}
        </div>
      </section>
    );
  };

  return (
    <div className="relative flex min-w-0 flex-1 items-center gap-1.5 sm:w-auto sm:flex-none" onBlur={closeOnBlur}>
      <span id={labelId} className="sr-only shrink-0 text-gray-600 sm:not-sr-only">{label}:</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? `${id}-option-${options[activeIndex]?.value ?? value}` : undefined}
        aria-labelledby={`${labelId} ${valueId}`}
        onClick={() => {
          setActiveIndex(selectedIndex);
          setOpen((previous) => !previous);
        }}
        onKeyDown={handleKeyDown}
        className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-xl border border-meadow-200 bg-white px-2.5 font-bold text-meadow-700 outline-none transition hover:bg-meadow-50 focus:border-meadow-500 focus:ring-2 focus:ring-meadow-100 sm:min-w-[158px] sm:flex-none"
      >
        <PetOriginMark option={selected} compact />
        <span id={valueId} className="min-w-0 flex-1 truncate text-left">{selected.label}</span>
        {selected.count !== undefined && (
          <span className="shrink-0 rounded-full bg-meadow-100 px-1.5 py-0.5 text-[10px] font-extrabold text-meadow-700">
            {selected.count}
          </span>
        )}
        <Icon src="/ui/ui_btn_menu_chevron.webp" fallback="v" alt="" className="h-3.5 w-3.5 opacity-70" />
      </button>

      {open && (
        <div
          role="listbox"
          id={listboxId}
          aria-labelledby={labelId}
          className="fixed inset-x-3 top-1/2 z-[70] max-h-[78dvh] -translate-y-1/2 overflow-y-auto rounded-2xl border border-meadow-200 bg-white p-2.5 shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:max-h-[min(32rem,70vh)] sm:w-[26rem] sm:translate-y-0"
        >
          <div className="mb-1 px-1">
            <span className="text-xs font-extrabold text-meadow-800">Wybierz świat peta</span>
          </div>
          <div role="group" aria-label="Szybki wybór" className="grid grid-cols-3 gap-2">
            {options.filter((option) => option.group === 'quick').map((option) => renderTile(option, true))}
          </div>
          {renderSection('worlds', 'Światy')}
          {renderSection('special', 'Specjalne')}
          {renderSection('events', 'Eventy')}
        </div>
      )}
    </div>
  );
}

function PetOriginMark({
  option,
  compact = false,
}: {
  option: OriginVisualOption;
  compact?: boolean;
}): JSX.Element {
  const visual = option.biomeId ? getPetOriginBiomeVisual(option.biomeId) : undefined;
  const textureKey = visual?.textureKey ?? option.backgroundTextureKey;
  const biomeSrc = option.biomeId && textureKey
    ? biomeEnvironmentAssetUrl(textureKey, option.biomeId)
    : undefined;
  const quickIcon = option.kind === 'all'
    ? { src: '/ui/ui_btn_pets.webp', fallback: 'W', background: '#FDE2E2' }
    : option.kind === 'worlds'
      ? { src: '/ui/ui_btn_map.webp', fallback: 'Ś', background: '#E0F2FE' }
      : { src: '/ui/ui_sparkle.webp', fallback: 'E', background: '#FEF3C7' };
  const src = biomeSrc ?? option.iconSrc ?? quickIcon.src;
  const fallback = visual?.fallback ?? (biomeSrc ? option.label.slice(0, 1) : quickIcon.fallback);
  const backgroundColor = option.accentColor
    ? `${option.accentColor}55`
    : quickIcon.background;
  const wrapperSize = compact ? 'h-9 w-9 rounded-lg' : 'h-11 w-11 rounded-xl';
  const imageSize = visual?.fit === 'cover'
    ? 'h-full w-full object-cover'
    : compact
      ? 'h-8 w-8 object-contain p-0.5'
      : 'h-10 w-10 object-contain p-0.5';

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden shadow-sm ring-1 ring-black/5 ${wrapperSize}`}
      style={{ backgroundColor }}
      aria-hidden="true"
    >
      <Icon src={src} fallback={fallback} alt="" className={`${imageSize} drop-shadow-sm`} />
      {visual?.badge === 'europe' && (
        <span className="absolute bottom-0.5 right-0.5 grid h-4 w-4 grid-cols-2 place-items-center rounded-full bg-blue-700 p-0.5 shadow ring-1 ring-white">
          <span className="h-1 w-1 rounded-full bg-yellow-300" />
          <span className="h-1 w-1 rounded-full bg-yellow-300" />
          <span className="h-1 w-1 rounded-full bg-yellow-300" />
          <span className="h-1 w-1 rounded-full bg-yellow-300" />
        </span>
      )}
    </span>
  );
}

function VisualSelectControl({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly VisualOption[];
}): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((opt) => opt.value === value) ?? options[0];
  const selectedIndex = Math.max(0, options.findIndex((opt) => opt.value === selected.value));
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const listboxId = `${id}-listbox`;
  const closeOnBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
      setOpen(false);
    }
  };
  const openAt = (index: number): void => {
    setActiveIndex(index);
    setOpen(true);
  };
  const selectAt = (index: number): void => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  };
  const moveActive = (direction: 1 | -1): void => {
    setActiveIndex((prev) => {
      const next = prev + direction;
      if (next < 0) return options.length - 1;
      if (next >= options.length) return 0;
      return next;
    });
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) openAt(selectedIndex);
        else moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) openAt(selectedIndex);
        else moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        openAt(0);
        break;
      case 'End':
        event.preventDefault();
        openAt(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) selectAt(activeIndex);
        else openAt(selectedIndex);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <div className="relative flex min-w-0 flex-1 items-center gap-1.5 sm:flex-none" onBlur={closeOnBlur}>
      <span id={labelId} className="sr-only text-gray-600 sm:not-sr-only">{label}:</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? `${id}-option-${options[activeIndex]?.value ?? value}` : undefined}
        aria-labelledby={`${labelId} ${valueId}`}
        onClick={() => {
          setActiveIndex(selectedIndex);
          setOpen((prev) => !prev);
        }}
        onKeyDown={handleKeyDown}
        className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg border border-meadow-200 bg-white px-2.5 font-bold text-meadow-700 outline-none transition hover:bg-meadow-50 focus:border-meadow-500 sm:min-w-[132px] sm:flex-none"
      >
        {selected.icon}
        <span id={valueId} className="min-w-0 flex-1 truncate text-left">{selected.label}</span>
        {selected.count !== undefined && (
          <span className="shrink-0 rounded-full bg-meadow-100 px-1.5 py-0.5 text-[10px] font-extrabold text-meadow-700">
            {selected.count}
          </span>
        )}
        <Icon src="/ui/ui_btn_menu_chevron.webp" fallback="v" alt="" className="h-3.5 w-3.5 opacity-70" />
      </button>
      {open && (
        <div
          role="listbox"
          id={listboxId}
          aria-labelledby={labelId}
          className="fixed inset-x-3 top-1/2 z-[70] max-h-[78dvh] -translate-y-1/2 overflow-y-auto rounded-xl border border-meadow-200 bg-white p-1 shadow-xl sm:absolute sm:inset-x-auto sm:left-0 sm:top-full sm:mt-1 sm:min-w-full sm:translate-y-0"
        >
          {options.map((opt, index) => {
            const active = opt.value === value;
            const focused = index === activeIndex;
            return (
              <button
                key={opt.value}
                id={`${id}-option-${opt.value}`}
                type="button"
                role="option"
                aria-selected={active}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex min-h-10 w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-bold transition ${
                  active
                    ? 'bg-meadow-500 text-white'
                    : focused
                      ? 'bg-meadow-100 text-meadow-800'
                      : 'text-meadow-700 hover:bg-meadow-50'
                } ${opt.muted && !active ? 'opacity-70' : ''}`}
              >
                {opt.icon}
                <span className="whitespace-nowrap">{opt.label}</span>
                {opt.count !== undefined && (
                  <span
                    className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${
                      active
                        ? 'bg-white/20 text-white'
                        : opt.count === 0
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-meadow-100 text-meadow-700'
                    }`}
                  >
                    {opt.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AllMarks(): JSX.Element {
  return (
    <span className="grid h-6 w-6 shrink-0 grid-cols-2 gap-0.5" aria-hidden="true">
      <span className="rounded-sm bg-gray-400" />
      <span className="rounded-sm bg-green-500" />
      <span className="rounded-sm bg-blue-500" />
      <span className="rounded-sm bg-yellow-500" />
    </span>
  );
}

function SortMark({ value }: { value: PetCollectionSort }): JSX.Element {
  switch (value) {
    case 'power':
      return <IconMark src="/ui/ui_power.webp" fallback="P" />;
    case 'mergeProgress':
      return <IconMark src="/ui/ui_btn_merge.webp" fallback="M" />;
    case 'rarity':
      return <IconMark src="/ui/ui_sparkle.webp" fallback="R" />;
    case 'variant':
      return <VariantMark value="rainbow" />;
    case 'acquired':
      return <IconMark src="/ui/ui_hourglass.webp" fallback="N" />;
    case 'name':
      return <NameSortMark />;
    case 'priceAsc':
      return <PriceMark direction="up" />;
    case 'priceDesc':
      return <PriceMark direction="down" />;
  }
}

function IconMark({ src, fallback }: { src: string; fallback: string }): JSX.Element {
  return (
    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white shadow-sm" aria-hidden="true">
      <Icon src={src} fallback={fallback} alt="" className="h-5 w-5" />
    </span>
  );
}

function PriceMark({ direction }: { direction: 'up' | 'down' }): JSX.Element {
  return (
    <span className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-purple-50 shadow-sm" aria-hidden="true">
      <Icon src="/ui/ui_gem.webp" fallback="G" alt="" className="h-4 w-4" />
      <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-purple-600 text-[9px] font-extrabold text-white">
        {direction === 'up' ? '↑' : '↓'}
      </span>
    </span>
  );
}

function NameSortMark(): JSX.Element {
  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-sky-100 text-[9px] font-black tracking-tight text-sky-800 shadow-sm"
      aria-hidden="true"
    >
      A–Z
    </span>
  );
}

export function RarityMark({ value }: { value: string }): JSX.Element {
  const styles: Record<string, { label: string; className: string }> = {
    common: { label: 'C', className: 'bg-gray-400 text-white' },
    uncommon: { label: 'U', className: 'bg-green-500 text-white' },
    rare: { label: 'R', className: 'bg-blue-500 text-white' },
    epic: { label: 'E', className: 'bg-purple-500 text-white' },
    legendary: { label: 'L', className: 'bg-yellow-500 text-white' },
    mythic: { label: 'M', className: 'bg-red-500 text-white' },
  };
  const style = styles[value] ?? styles.common;
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-extrabold shadow-sm ${style.className}`}
      aria-hidden="true"
    >
      {style.label}
    </span>
  );
}

export function VariantMark({ value }: { value: string }): JSX.Element {
  if (value === 'rainbow') {
    return (
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white shadow-sm">
        <Icon src="/ui/ui_variant_rainbow.webp" fallback="R" alt="" className="h-5 w-5" />
      </span>
    );
  }

  const styles: Record<string, { label: string; className: string }> = {
    normal: { label: 'N', className: 'bg-gray-300 text-gray-900' },
    gold: { label: 'G', className: 'bg-yellow-400 text-yellow-950' },
    huge: { label: 'H', className: 'bg-red-500 text-white' },
    titanic: {
      label: 'T',
      className: 'bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 text-white',
    },
  };
  const style = styles[value] ?? styles.normal;
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-extrabold shadow-sm ${style.className}`}
      aria-hidden="true"
    >
      {style.label}
    </span>
  );
}

function MergeButton({
  value,
  current,
  label,
  onClick,
}: {
  value: PetMergeMode;
  current: PetMergeMode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-2 py-1 font-bold transition active:scale-95 ${
        active
          ? 'bg-purple-600 text-white'
          : 'bg-white text-meadow-700 hover:bg-meadow-100'
      }`}
    >
      {label}
    </button>
  );
}
